import { isEmpty } from 'lodash'
import * as pdfjsLib from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry'
import heic2any from 'heic2any'
import store from '../../store'
import { FLOOR_PLAN_MAX_SIZE, SOFTWARE_PLANS } from '../root/constants'
import loadImage from 'blueimp-load-image'

export const compressBigImage = file => {
    return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
            return reject(new Error('File is not an image'))
        }
        if (file.size <= FLOOR_PLAN_MAX_SIZE) {
            resolve(file)
            return
        }
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onerror = () => {
            return reject(new Error('Failed to read file'))
        }
        reader.onload = e => {
            const img = new Image()
            img.src = e.target.result
            img.onerror = () => {
                return reject(new Error('Failed to load image'))
            }
            img.onload = () => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')

                // Start with original dimensions
                let width = img.width
                let height = img.height

                // Maximum dimensions while maintaining aspect ratio
                const MAX_WIDTH = 2048
                const MAX_HEIGHT = 2048

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height = Math.round((height * MAX_WIDTH) / width)
                        width = MAX_WIDTH
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width = Math.round((width * MAX_HEIGHT) / height)
                        height = MAX_HEIGHT
                    }
                }

                canvas.width = width
                canvas.height = height

                ctx.drawImage(img, 0, 0, width, height)

                // Start with high quality
                let quality = 0.9
                const minQuality = 0.1

                function compressBlob() {
                    canvas.toBlob(
                        blob => {
                            if (
                                blob.size > FLOOR_PLAN_MAX_SIZE &&
                                quality > minQuality
                            ) {
                                // Reduce quality and try again
                                quality -= 0.1
                                compressBlob()
                            } else {
                                resolve(blob)
                            }
                        },
                        file.type,
                        quality
                    )
                }

                compressBlob()
            }
        }
    })
}

export const dataURItoBlob = dataURI => {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
        byteString = atob(dataURI.split(',')[1])
    } else {
        byteString = unescape(dataURI.split(',')[1])
    }
    // separate out the mime component
    const mimeString = dataURI
        .split(',')[0]
        .split(':')[1]
        .split(';')[0]

    // write the bytes of the string to a typed array
    let ia = new Uint8Array(byteString.length)
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
    }

    return new Blob([ia], { type: mimeString })
}

export const processFile = acceptedFiles => {
    return new Promise((res, rej) => {
        acceptedFiles.forEach(file => {
            if (['image/heic', 'image/heif'].includes(file.type)) {
                return heic2any({
                    blob: file,
                    toType: 'image/jpeg',
                    quality: 0.8
                })
                    .then(response => {
                        return res({ file: response })
                    })
                    .catch(rej)
            } else if (file.type === 'application/pdf') {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onloadend = () => {
                    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker
                    pdfjsLib.getDocument(reader.result).promise.then(pdf => {
                        if (pdf.numPages > 0) {
                            pdf.getPage(1).then(page => {
                                const viewportParameters = { scale: 4.0 }
                                const viewport = page.getViewport(
                                    viewportParameters
                                )
                                const canvas = document.createElement('canvas')
                                const canvasContext = canvas.getContext('2d')
                                canvas.height = viewport.height
                                canvas.width = viewport.width
                                page.render({
                                    canvasContext,
                                    viewport
                                })
                                    .promise.then(() => {
                                        return res({
                                            file: dataURItoBlob(
                                                canvas.toDataURL('image/jpeg')
                                            )
                                        })
                                    })
                                    .catch(rej)
                            })
                        }
                    })
                }
            } else {
                return loadImage(file, { canvas: true })
                    .then(data => {
                        data.image.toBlob(blob => {
                            res({ file: blob })
                        })
                    })
                    .catch(e => {
                        res({
                            file
                        })
                    })
            }
        })
    })
}

export const groupDevicesByLocation = devices => {
    if (isEmpty(devices)) return null
    return devices.reduce((acc, device) => {
        if (!acc.hasOwnProperty(device.location_id)) {
            acc[device.location_id] = {
                locationName: device.location_name,
                devices: []
            }
        }
        acc[device.location_id].devices.push(device)
        return acc
    }, {})
}

export const isFreeSoftwarePlan = () => {
    const { user } = store.getState().main
    return user.original_software_plan === SOFTWARE_PLANS.free
}

export const dispatchFloorplanError = msg =>
    window.ReactNativeWebView?.postMessage(`error-${msg}`)

export const dispatchFloorplanDevLog = msg => {
    if (typeof msg === 'object') msg = JSON.stringify(msg, null, 2)
    console.log('::DEV INNER LOG::', `\n`, msg)
    window.ReactNativeWebView?.postMessage(`dev_log||||||${msg}`)
}
